import { Component, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { Offer } from '../../models/offers.model';
import { DataLayerService } from 'src/app/shared/services/data-layer.service';
import { OffersForm, OffersPayload } from '../../models/offers-form.model';
import { ToFormControls } from 'src/app/utils/form-control-type';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, take, takeUntil } from 'rxjs';
import { CustomUser } from './model/offer.model';

type OfferForm = ToFormControls<OffersForm>;

@Component({
    selector: 'app-offer-form',
    templateUrl: './offer-form.component.html',
    styleUrl: './offer-form.component.scss',
    standalone: false
})
export class OfferFormComponent implements OnInit, OnDestroy {
  readonly offer = input.required<Offer>();
  readonly user = input.required<CustomUser>();

  readonly offerFormSubmission = output<OffersPayload>();

  private readonly dataLayerService = inject(DataLayerService);
  private readonly formBuilder = inject(FormBuilder);

  private readonly destroy$ = new Subject<void>();

  offerForm: FormGroup<OfferForm>;

  ngOnInit(): void {
    this.offerForm = this.formBuilder.group<OfferForm>({
      email: new FormControl(
        { value: this.user().email, disabled: true },
        { validators: [Validators.required] }
      ),
      userName: new FormControl(this.user().name, {
        validators: [
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-Z0-9\s'-]*$/),
        ],
      }),
      userRole: new FormControl(this.user().role, {
        validators: [
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-Z\s/'-]*$/),
        ],
      }),
      offersMap: new FormControl(null, {
        validators: [Validators.required],
      }),
      authorizeOffer: new FormControl(false, {
        validators: [Validators.requiredTrue],
      }),
    });

    this.offerForm.valueChanges
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.dataLayerService.addtoDataLayer(
            'offerForm_FormEvent',
            null,
            null,
            null,
            'form_start'
          );
        },
      });
  }

  confirmOffer(): void {
    const formValue = this.offerForm.getRawValue();
    const selectedTraitId = formValue.offersMap;

    const offersPayload: OffersPayload = {
      email: formValue.email,
      userName: formValue.userName,
      userRole: formValue.userRole,
      offersMap: [
        // always include parent offerId
        { key: this.offer().offerId, value: true },

        // dynamically map over all traits
        ...this.offer().offerTraits.map((trait) => ({
          key: trait.traitId,
          value: trait.traitId === selectedTraitId,
        })),
      ],
    };

    this.offerFormSubmission.emit(offersPayload);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
